import { type FC, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ConfirmationModal, ConfirmationModalType, Select } from '@wf/components';
import { Label } from '@wf/sw-ui-kit';

import { LicenseField } from '../LicenseField';

import useOperationName from '../../../../hooks/useOperationName';
import { supportInfoMessage } from '../../types/fieldEnums';
import { ConfigOption } from '../../types/configs';
import { OperationType } from '../../types/enums';

import {
  operationMessagesSelector,
  queryMessageSelector,
  supportInfoMessageConfigByOperationSelector,
} from '../../store/selectors';
import { replaceQueryMessage } from '../../store/slice';

import styles from './OperationConfirmationModal.module.scss';

interface IOperationConfirmationModalProps {
  onCancel: () => void;
  onAccept: () => void;
  operation: OperationType;
}

const OperationConfirmationModal: FC<IOperationConfirmationModalProps> = ({ onCancel, onAccept, operation }) => {
  const { t, i18n } = useTranslation();
  const queryMessage = useSelector(queryMessageSelector);

  const dispatch = useDispatch();

  const {
    formState: { errors },
    trigger,
    setValue,
    getValues,
  } = useFormContext();

  const [selectedDivision, setSelectedDivision] = useState<SelectBaseOption | null>(null);
  const [isDivisionSelected, setIsDivisionSelected] = useState(false);
  const formValues = getValues();
  const assignableDivisions = formValues.assignableDivisions ?? [];
  const divisionOptions = assignableDivisions.map((division: string) => ({
    label: division,
    value: division,
  }));

  const handleDivisionSelect = (option: SelectBaseOption | null) => {
    setSelectedDivision(option);
    setIsDivisionSelected(true);

    if (option?.value) {
      setValue('departmentDivisionCode', option.value);
    }
  };

  const operationMessages = useSelector(operationMessagesSelector);
  const messageOptions = operationMessages
    .filter(({ operation: messageOperation }) => messageOperation === operation)
    .map(({ message, messageTranslated }) => {
      const value = i18n.language === 'en' ? message : messageTranslated;

      return {
        value,
        label: value,
      };
    });

  const supportInfoMessageConfig = useSelector(supportInfoMessageConfigByOperationSelector(operation));
  const getOperationName = useOperationName();

  const showSupportInfoContent = supportInfoMessageConfig !== ConfigOption.PROHIBITED;

  useEffect(() => {
    if (operation === OperationType.QUERY && queryMessage.length) {
      setValue(supportInfoMessage, queryMessage);

      return () => {
        // if user has done some changes in query message and closed popup
        // need to show changed message when next time opening it
        dispatch(replaceQueryMessage(getValues(supportInfoMessage) as string));
      };
    }
  }, []);

  useEffect(() => {
    // remove error that comes from previous operation
    errors[supportInfoMessage] && void trigger(supportInfoMessage);

    return () => {
      // remove input value from previous operation
      setValue(supportInfoMessage, '');
    };
  }, []);

  const handleMessageSelect = (message: SelectBaseOption) => {
    if (message?.value) {
      const currentMessage = getValues(supportInfoMessage) as string;
      const newMessage = currentMessage ? `${currentMessage}\n${message.value}` : message.value;
      setValue(supportInfoMessage, newMessage);
    }
  };

  const operationName = getOperationName(operation);
  const isAssignOperation =
    assignableDivisions.length > 0 &&
    operation !== OperationType.RETURN &&
    operation !== OperationType.QUERY &&
    operation !== OperationType.REJECT;

  return (
    <ConfirmationModal
      isAcceptDisabled={isAssignOperation && !isDivisionSelected}
      type={ConfirmationModalType.Addition}
      title={t('Operation Confirmation')}
      className={styles.container}
      acceptText={t('Confirm')}
      cancelText={t('Close')}
      id="confirmation-modal"
      onAccept={onAccept}
      onCancel={onCancel}
      isOpen
    >
      <div className={styles.modalContent}>
        <span>
          {isAssignOperation
            ? t('Select who will process the CLP at this stage')
            : t('Please confirm you want to perform {{operationName}} operation.', { operationName })}
        </span>

        {isAssignOperation && divisionOptions.length > 0 && (
          <Select
            label={<Label isMandatory={true}>{t('Assign to')}</Label>}
            placeholder={t('Select assignee')}
            className={styles.selectAssignee}
            onChange={handleDivisionSelect}
            options={divisionOptions}
            value={selectedDivision}
            isClearable={false}
            isSearchable
          />
        )}
        {showSupportInfoContent && (
          <>
            <LicenseField.TextField
              label={<Label isMandatory={supportInfoMessageConfig === ConfigOption.MANDATORY}>{t('Message')}</Label>}
              configName={supportInfoMessage}
              name={supportInfoMessage}
              className={styles.input}
              textarea
            />
            {messageOptions.length > 0 && (
              <Select
                placeholder={t('Choose from samples')}
                className={styles.messageSamples}
                onChange={handleMessageSelect}
                options={messageOptions}
                value={null}
              />
            )}
          </>
        )}
      </div>
    </ConfirmationModal>
  );
};

export default OperationConfirmationModal;
